import { useState, useEffect } from 'react';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
}

export const useAuth = (): AuthState & { 
  login: () => void; 
  logout: () => void; 
  checkAuth: () => void; 
  renewToken: () => Promise<boolean>; 
} => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Check server authentication status (handles SSO and cookie-based auth)
      const response = await fetch('/api/auth/status', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.authenticated) {
          // User is authenticated on server, sync with localStorage
          localStorage.setItem('cascade-auth', 'authenticated');
          localStorage.setItem('cascade-auth-time', Date.now().toString());
          setIsAuthenticated(true);
        } else {
          // Not authenticated on server, clear localStorage (handles post-logout cleanup)
          localStorage.removeItem('cascade-auth');
          localStorage.removeItem('cascade-auth-time');
          setIsAuthenticated(false);
        }
      } else {
        // Server check failed, clear auth
        localStorage.removeItem('cascade-auth');
        localStorage.removeItem('cascade-auth-time');
        setIsAuthenticated(false);
      }
    } catch (error) {
      // Don't log to Application Insights - this is normal auth flow behavior
      console.warn('Auth check failed:', error);
      localStorage.removeItem('cascade-auth');
      localStorage.removeItem('cascade-auth-time');
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async () => {
    // Session-based authentication - check with server
    try {
      const response = await fetch('/api/auth/status', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.authenticated) {
          localStorage.setItem('cascade-auth', 'authenticated');
          localStorage.setItem('cascade-auth-time', Date.now().toString());
          setIsAuthenticated(true);
        } else {
          throw new Error('Not authenticated');
        }
      } else {
        throw new Error('Authentication check failed');
      }
    } catch (error) {
      throw new Error('Authentication check failed');
    }
  };

  const logout = async () => {
    // Don't clear auth state or localStorage yet - just redirect
    // The server will handle the full logout flow and redirect back to /
    // Then the auth check will naturally fail and show the login page
    window.location.href = '/api/auth/logout';
  };

  // Token renewal function - call this periodically
  const renewToken = async () => {
    if (!isAuthenticated) return false;
    
    try {
      const response = await fetch('/api/auth/status', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.authenticated) {
          // Session is still valid, update local timestamp
          localStorage.setItem('cascade-auth-time', Date.now().toString());
          return true;
        } else {
          // Session expired on server
          logout();
          return false;
        }
      } else {
        // Server check failed
        return false;
      }
    } catch (error) {
      // Don't log to Application Insights - token renewal failures are expected
      console.warn('Token renewal failed:', error);
      return false;
    }
  };

  return {
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth: checkAuthStatus,
    renewToken
  };
};
